import {
  buildMfaTwoFactorFromSession,
  checkRateLimit,
  clearMfaFailedAttempts,
  computeMfaLockedOut,
  createInitialTwoFactorState,
  getMfaLockoutMessage,
  logAuditEvent,
  mfaAfterPasswordSignIn,
  mfaDisable,
  mfaDisableSendChallenge,
  mfaDisableVerifyAndUnenroll,
  mfaEnrollTotp,
  mfaEnrollVerify,
  mfaGenerateRecoveryCodes,
  mfaVerifyLoginChallenge,
  mfaAfterRecoveryVerifySuccess,
  mfaVerifyRecoveryCode,
  MFA_MAX_FAILED_ATTEMPTS,
  recordMfaFailedAttempt,
  resetRateLimit,
  sanitizeEmail,
  saveLoginTimestamp,
  type MfaPendingSession,
  type MfaRateLimitState,
  type TwoFactorState,
} from "@dwelldrop/shared";
import { getSupabase } from "@dwelldrop/supabase";
import type { Dispatch, SetStateAction } from "react";
import { ADMIN_MFA_CONFIG } from "./adminMfaConfig";

export type AdminMfaProfile = {
  id: string;
  phone: string | null;
};

export type AdminPendingSession = MfaPendingSession<AdminMfaProfile>;

export async function fetchAdminMfaProfile(
  userId: string
): Promise<AdminMfaProfile | null> {
  const supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
  const { data, error } = await supabase
    .from("profiles")
    .select("id, phone, role, deleted_at")
    .eq("id", userId)
    .single();
  if (error || !data || data.deleted_at || data.role !== "admin") {
    return null;
  }
  return { id: data.id, phone: data.phone };
}

export type AdminSignInParams = {
  email: string;
  password: string;
  rateLimit: MfaRateLimitState;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
  isLockedOut: () => boolean;
  setPendingSession: Dispatch<SetStateAction<AdminPendingSession | null>>;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  signInInProgress: { current: boolean };
  rememberMe?: boolean;
};

export async function adminSignInWithPassword(
  params: AdminSignInParams
): Promise<{
  error: string | null;
  needs2FA: boolean;
  retryAfterSeconds?: number;
}> {
  const {
    email,
    password,
    rateLimit,
    setRateLimit,
    isLockedOut,
    setPendingSession,
    setTwoFactor,
    signInInProgress,
    rememberMe = false,
  } = params;

  if (isLockedOut()) {
    const remainingSec = rateLimit.lockedUntil
      ? Math.max(0, Math.ceil((rateLimit.lockedUntil - Date.now()) / 1000))
      : 900;
    return {
      error: getMfaLockoutMessage(rateLimit.lockedUntil),
      needs2FA: false,
      retryAfterSeconds: remainingSec,
    };
  }

  const supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
  const cleanEmail = sanitizeEmail(email);

  signInInProgress.current = true;

  try {
    const rl = await checkRateLimit(
      supabase,
      ADMIN_MFA_CONFIG.loginRateKey(cleanEmail),
      "login"
    );
    if (!rl.allowed) {
      const retrySeconds = rl.retryAfterSeconds ?? 900;
      setRateLimit((r) => ({
        ...r,
        failedAttempts: MFA_MAX_FAILED_ATTEMPTS,
        lockedUntil: Date.now() + retrySeconds * 1000,
      }));
      await logAuditEvent(
        supabase,
        "rate_limit_exceeded",
        null,
        { email: cleanEmail, app: "admin" },
        "auth",
        ADMIN_MFA_CONFIG.auditActionSource()
      );
      return {
        error: `Too many login attempts. Please try again in ${Math.ceil(retrySeconds / 60)} minutes.`,
        needs2FA: false,
        retryAfterSeconds: retrySeconds,
      };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      recordMfaFailedAttempt(setRateLimit);
      await logAuditEvent(
        supabase,
        "login_failure",
        null,
        { email: cleanEmail, reason: error.message, app: "admin" },
        "auth",
        ADMIN_MFA_CONFIG.auditActionSource()
      );
      return { error: error.message, needs2FA: false };
    }

    if (!data.session || !data.user) {
      return { error: "Login failed. Please try again.", needs2FA: false };
    }

    const profile = await fetchAdminMfaProfile(data.user.id);
    if (!profile) {
      await supabase.auth.signOut();
      return {
        error: "Access denied. This portal is for administrators only.",
        needs2FA: false,
      };
    }

    clearMfaFailedAttempts(setRateLimit);

    const mfaResult = await mfaAfterPasswordSignIn({
      config: ADMIN_MFA_CONFIG,
      userId: data.user.id,
      profile,
      rateLimit,
      setRateLimit,
      setPendingSession,
      setTwoFactor,
      rememberMe,
      user: data.user,
      session: data.session,
    });

    if (mfaResult.needsMfaChallenge) {
      return { error: null, needs2FA: true };
    }

    await resetRateLimit(supabase, ADMIN_MFA_CONFIG.loginRateKey(cleanEmail));
    await logAuditEvent(
      supabase,
      "login_success",
      data.user.id,
      { app: "admin" },
      "auth",
      ADMIN_MFA_CONFIG.auditActionSource()
    );
    saveLoginTimestamp("venuehub_admin", rememberMe);

    const twoFactorBits = await buildMfaTwoFactorFromSession(
      ADMIN_MFA_CONFIG.tokenStorageKey,
      profile.phone
    );
    setTwoFactor({
      ...createInitialTwoFactorState(),
      ...twoFactorBits,
    });
    setPendingSession(null);

    return { error: null, needs2FA: false };
  } finally {
    signInInProgress.current = false;
  }
}

export function createAdminMfaHandlers(deps: {
  pendingSession: AdminPendingSession | null;
  rateLimit: MfaRateLimitState;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
  setPendingSession: Dispatch<SetStateAction<AdminPendingSession | null>>;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  isLockedOut: () => boolean;
  twoFactor: TwoFactorState;
  onMfaLoginComplete: (
    pending: AdminPendingSession,
    session: import("@dwelldrop/supabase").Session
  ) => void;
  userId: string | null;
}) {
  const {
    pendingSession,
    rateLimit,
    setRateLimit,
    setPendingSession,
    setTwoFactor,
    isLockedOut,
    twoFactor,
    onMfaLoginComplete,
    userId,
  } = deps;

  return {
    verify2FA: (code: string) => {
      const factorId = twoFactor.mfaFactorId;
      const challengeId = twoFactor.mfaChallengeId;
      if (!factorId || !challengeId) {
        return Promise.resolve({
          error: "MFA challenge expired. Please sign in again.",
        });
      }
      return mfaVerifyLoginChallenge({
        config: ADMIN_MFA_CONFIG,
        code,
        factorId,
        challengeId,
        pendingSession,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
        setRateLimit,
        setPendingSession,
        setTwoFactor,
        onLoginSuccess: onMfaLoginComplete,
      });
    },
    enable2FAEnrollTotp: () => mfaEnrollTotp({ config: ADMIN_MFA_CONFIG }),
    enable2FAVerify: (params: {
      code: string;
      factorId: string;
      challengeId?: string;
      phone?: string;
    }) =>
      mfaEnrollVerify({
        config: ADMIN_MFA_CONFIG,
        code: params.code,
        factorId: params.factorId,
        challengeId: params.challengeId,
        phone: params.phone,
        userId,
        setTwoFactor,
      }),
    disable2FA: () => mfaDisable(ADMIN_MFA_CONFIG, userId, setTwoFactor),
    disable2FASendChallenge: () =>
      mfaDisableSendChallenge({
        config: ADMIN_MFA_CONFIG,
        userId,
        rateLimit,
        setRateLimit,
        setTwoFactor,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
      }),
    disable2FAVerify: (params: {
      code: string;
      factorId: string;
      challengeId: string;
    }) => {
      const { code, factorId, challengeId } = params;
      if (!factorId || !challengeId) {
        return Promise.resolve({
          error: "Verification expired. Request a new code and try again.",
        });
      }
      return mfaDisableVerifyAndUnenroll({
        config: ADMIN_MFA_CONFIG,
        code,
        factorId,
        challengeId,
        userId,
        setTwoFactor,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
        setRateLimit,
      });
    },
    generateRecoveryCodes: () => mfaGenerateRecoveryCodes(ADMIN_MFA_CONFIG),
    verifyRecoveryCode: async (code: string) => {
      const result = await mfaVerifyRecoveryCode(ADMIN_MFA_CONFIG, code);
      if (result.error) return result;
      await mfaAfterRecoveryVerifySuccess({
        config: ADMIN_MFA_CONFIG,
        setTwoFactor,
        clearPendingSession: () => setPendingSession(null),
      });
      return { error: null };
    },
  };
}

export { computeMfaLockedOut as adminComputeMfaLockedOut };
